/*
  Herzwarm Akkordeons (HWSHOP-32, 24.10.2026)
  Macht die Produktbeschreibung (Rich Text) zu aufklappbaren Abschnitten mit einzeiliger Vorschau.
  Progressive Enhancement: ohne JavaScript bleibt der Text vollständig sichtbar. Inhalt bleibt im
  DOM (Suchmaschinen, Screenreader). Alle Abschnitte starten zugeklappt; der Kreatin-Warnhinweis
  („Hinweis zum Verzehr“) stand bis 30.10. offen und ist seitdem ebenfalls zu, weil er auf jeder
  Produktseite vollständig sichtbar im Block „Gut zu wissen“ (hw-produkt-hinweise) steht.
  Abschnittsgrenzen: jede H2/H3 im Beschreibungstext bis zur nächsten H2/H3. Absätze, die nur aus
  Links bestehen, und der KI-Bildhinweis bleiben außerhalb sichtbar; auf Produktseiten bekommen sie
  die Klassen hw-linkzeile bzw. hw-kizeile (kleine Schrift, assets/hw-tabs.css). Die Vorschauzeile
  ist auf Produktseiten per CSS ausgeblendet (30.10.), im Magazin sichtbar.
  Zusätzlich: Magazin-Artikel (gleiche Logik), Kacheln „Ehrlich gesagt“/„Unsere Haltung“ (Titel
  sichtbar, Text aufklappbar) und lange Einleitungstexte im Seitenkopf („Weiterlesen“).
  Barrierefreiheit (HWSHOP-32 Review): Disclosure-Muster statt <details>/<summary>. Die Überschrift
  bleibt Überschrift (summary hätte die Rolle „button“ und ihre Semantik verdeckt), darin sitzt ein
  <button> mit aria-expanded/aria-controls. Zugeklappter Inhalt trägt hidden="until-found", damit die
  Seitensuche ihn findet und aufklappt. Nach dem Varianten-Morph von Horizon (Server-HTML ersetzt
  den Umbau) baut ein MutationObserver die Akkordeons neu auf.
  Sorten-Tabs (29.10.2026, nur Produktseiten): Eine H2 mit mehreren H3 darunter, die alle mit
  „Suppe“ oder „Porridge“ beginnen (Sets: Zutaten, Allergene und Nährwerte je Sorte), wird ein
  Akkordeon mit waagerechten Sorten-Tabs statt eines Akkordeons je Sorte. Andere H3-Gruppen
  (Einzelsorten: „Hinweis zum Verzehr“, „Zutaten“ …) bleiben einzelne Akkordeons.
  Tab-Muster (WAI-ARIA): Pfeiltasten, Pos1/Ende; nicht gewählte Sorten tragen
  hidden="until-found" (Seitensuche wählt die Sorte). CSS in assets/hw-tabs.css.
*/
(() => {
  const KOPF = /^H[23]$/;

  const nurLinks = (el) => {
    if (el.tagName !== 'P') return false;
    const links = el.querySelectorAll('a');
    if (links.length < 2) return false;
    let rest = el.textContent;
    links.forEach((a) => { rest = rest.replace(a.textContent, ''); });
    return rest.replace(/[·\s]/g, '') === '';
  };
  // KI-Hinweis: ohne Groß-/Kleinschreibung; Leerraum, Bindestrich, geschütztes Leerzeichen (U+00A0)
  // und U+2010 bis U+2014 (geschützter Bindestrich, Halbgeviert-, Geviertstrich) gelten gleich
  // (gleiche Regel wie snippets/hw-serviervorschlag.liquid und snippets/card-gallery.liquid).
  // Sonderzeichen über ihre Codes, damit keine unsichtbaren Zeichen im Quelltext stehen.
  const KI_STRICHE = new RegExp('[' + String.fromCharCode(0x00a0, 0x2010, 0x2011, 0x2012, 0x2013, 0x2014) + ']', 'g');
  const KI = { test: (text) => /KI[\s-]*generiert/i.test(text.replace(KI_STRICHE, ' ')) };
  const bleibtDraussen = (el) => nurLinks(el) || KI.test(el.textContent);

  const vorschau = (nodes) => {
    const unter = nodes.filter((n) => /^H[4-6]$/.test(n.tagName)).map((n) => n.textContent.trim());
    if (unter.length > 1) return unter.join(' · ');
    if (nodes.some((n) => n.matches('table, .rte-table-wrapper') || n.querySelector('table'))) {
      // Keine feste Bezugsangabe: Tabellenbeschriftung (caption), sonst neutral „Tabelle“
      const tabelle = nodes.map((n) => (n.matches('table') ? n : n.querySelector('table'))).find(Boolean);
      const titel = tabelle && tabelle.caption ? tabelle.caption.textContent.replace(/\s+/g, ' ').trim() : '';
      return titel || 'Tabelle';
    }
    const t = nodes.map((n) => n.textContent).join(' ').replace(/\s+/g, ' ').trim();
    return t.length > 110 ? t.slice(0, 108).replace(/\s\S*$/, '') + ' …' : t;
  };

  // Disclosure: Knopf in die Überschrift (ziel), Zustand über [open] am Wrapper (CSS wie bisher)
  let akkNr = 0;
  const setzen = (akk, knopf, koerper, offen) => {
    akk.toggleAttribute('open', offen);
    knopf.setAttribute('aria-expanded', String(offen));
    if (offen) koerper.removeAttribute('hidden');
    else koerper.setAttribute('hidden', 'until-found');
  };
  const disclosure = (akk, kopf, ziel, koerper, offen) => {
    koerper.id = koerper.id || 'hw-akk-' + ++akkNr;
    const knopf = document.createElement('button');
    knopf.type = 'button';
    knopf.className = 'hw-akk__knopf';
    knopf.setAttribute('aria-controls', koerper.id);
    while (ziel.firstChild) knopf.appendChild(ziel.firstChild);
    ziel.appendChild(knopf);
    setzen(akk, knopf, koerper, offen);
    knopf.addEventListener('click', () => setzen(akk, knopf, koerper, !akk.hasAttribute('open')));
    // Klick auf Vorschau oder Pfeil klappt ebenfalls (wie vorher der ganze Kopf); Links bleiben Links
    kopf.addEventListener('click', (e) => {
      if (e.target.closest('a, button, input, select, textarea')) return;
      knopf.click();
    });
    // Seitensuche (Strg+F) findet zugeklappten Inhalt und klappt ihn auf
    koerper.addEventListener('beforematch', () => setzen(akk, knopf, koerper, true));
  };

  // Sorten-Überschrift (H3) erkennen und kürzen: „Suppe Tomate · 250 g“ -> „Tomate“
  const SORTE = /^(Suppe|Porridge)\s/i;
  const sorteKurz = (t) => t.replace(/\s*·.*$/, '').replace(/^(Suppe|Porridge)\s+/i, '').trim();
  let tabNr = 0;
  const sorten = (koerper, gruppen) => {
    const liste = document.createElement('div');
    liste.className = 'hw-tabs__liste';
    liste.setAttribute('role', 'tablist');
    const tabs = [];
    const panels = [];
    gruppen.forEach(({ kopf, inhalt }) => {
      const id = 'hw-tab-' + ++tabNr;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'hw-tabs__tab';
      tab.id = id + '-knopf';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id);
      tab.textContent = sorteKurz(kopf.textContent);
      const panel = document.createElement('div');
      panel.className = 'hw-tabs__panel';
      panel.id = id;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;
      kopf.classList.add('hw-tabs__kopf');
      panel.append(kopf, ...inhalt);
      liste.appendChild(tab);
      tabs.push(tab);
      panels.push(panel);
    });
    const waehlen = (n, fokus) => {
      tabs.forEach((tab, k) => {
        const an = k === n;
        tab.setAttribute('aria-selected', String(an));
        tab.tabIndex = an ? 0 : -1;
        if (an) panels[k].removeAttribute('hidden');
        else panels[k].setAttribute('hidden', 'until-found');
      });
      if (fokus) {
        tabs[n].focus();
        tabs[n].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };
    tabs.forEach((tab, k) => tab.addEventListener('click', () => waehlen(k, false)));
    liste.addEventListener('keydown', (e) => {
      const k = tabs.indexOf(document.activeElement);
      const ziel = { ArrowRight: k + 1, ArrowLeft: k - 1, Home: 0, End: tabs.length - 1 }[e.key];
      if (k < 0 || ziel === undefined) return;
      e.preventDefault();
      waehlen((ziel + tabs.length) % tabs.length, true);
    });
    panels.forEach((panel, k) => panel.addEventListener('beforematch', () => waehlen(k, false)));
    koerper.append(liste, ...panels);
    waehlen(0, false);
  };

  function baue(root) {
    if (!root || root.dataset.hwAkk) return;
    root.dataset.hwAkk = '1';
    const kinder = [...root.children];
    const produkt = !!root.closest('.product-details');
    // Produktseiten: Linkzeile und KI-Hinweis unter der Beschreibung klein setzen (assets/hw-tabs.css)
    if (produkt) {
      kinder.forEach((el) => {
        if (nurLinks(el)) el.classList.add('hw-linkzeile');
        else if (el.tagName === 'P' && KI.test(el.textContent)) el.classList.add('hw-kizeile');
      });
    }
    for (let i = 0; i < kinder.length; i++) {
      const kopf = kinder[i];
      if (!KOPF.test(kopf.tagName)) continue;

      // Sorten-Tabs: H2 mit mindestens zwei Sorten-H3 bis zur nächsten H2
      if (produkt && kopf.tagName === 'H2') {
        const davor = [];
        const gruppen = [];
        let j = i + 1;
        while (j < kinder.length && kinder[j].tagName !== 'H2' && !bleibtDraussen(kinder[j])) {
          const n = kinder[j];
          if (n.tagName === 'H3') gruppen.push({ kopf: n, inhalt: [] });
          else if (gruppen.length) gruppen[gruppen.length - 1].inhalt.push(n);
          else davor.push(n);
          j++;
        }
        if (gruppen.length > 1 && gruppen.every((g) => SORTE.test(g.kopf.textContent.trim()))) {
          const akk = document.createElement('div');
          akk.className = 'hw-akk hw-akk--sorten';
          const kopfBox = document.createElement('div');
          kopfBox.className = 'hw-akk__kopf';
          const text = document.createElement('span');
          text.className = 'hw-akk__vorschau';
          text.textContent = gruppen.map((g) => sorteKurz(g.kopf.textContent)).join(' · ');
          text.setAttribute('aria-hidden', 'true');
          const koerper = document.createElement('div');
          koerper.className = 'hw-akk__inhalt';
          root.insertBefore(akk, kopf);
          kopfBox.append(kopf, text);
          koerper.append(...davor);
          sorten(koerper, gruppen);
          akk.append(kopfBox, koerper);
          disclosure(akk, kopfBox, kopf, koerper, false);
          i = j - 1;
          continue;
        }
      }

      const inhalt = [];
      let j = i + 1;
      while (j < kinder.length && !KOPF.test(kinder[j].tagName) && !bleibtDraussen(kinder[j])) {
        inhalt.push(kinder[j]);
        j++;
      }
      if (!inhalt.length) continue;

      const akk = document.createElement('div');
      akk.className = 'hw-akk';
      const kopfBox = document.createElement('div');
      kopfBox.className = 'hw-akk__kopf';
      const text = document.createElement('span');
      text.className = 'hw-akk__vorschau';
      text.textContent = vorschau(inhalt);
      text.setAttribute('aria-hidden', 'true');
      const koerper = document.createElement('div');
      koerper.className = 'hw-akk__inhalt';

      root.insertBefore(akk, kopf);
      kopfBox.appendChild(kopf);
      kopfBox.appendChild(text);
      inhalt.forEach((n) => koerper.appendChild(n));
      akk.append(kopfBox, koerper);
      disclosure(akk, kopfBox, kopf, koerper, false);
      i = j - 1;
    }
  }

  function aussagen(scope) {
    scope.querySelectorAll('[id$="__ehrlich"] .hw-aussage, [id$="__haltung"] .hw-aussage').forEach((karte) => {
      const text = karte.querySelector(':scope > .hw-text');
      if (karte.dataset.hwAkk || !text) return;
      karte.dataset.hwAkk = '1';
      const akk = document.createElement('div');
      akk.className = 'hw-akk hw-akk--aussage';
      const kopfBox = document.createElement('div');
      kopfBox.className = 'hw-akk__kopf';
      [...karte.children].filter((c) => c !== text).forEach((c) => kopfBox.appendChild(c));
      // Knopf in die Titel-Überschrift der Kachel, sonst ins erste Kopf-Element
      const ziel = kopfBox.querySelector('h2, h3, h4, h5, h6, .hw-h3') || kopfBox.firstElementChild;
      const v = document.createElement('span');
      v.className = 'hw-akk__vorschau';
      v.setAttribute('aria-hidden', 'true');
      v.textContent = vorschau([text]);
      kopfBox.appendChild(v);
      const koerper = document.createElement('div');
      koerper.className = 'hw-akk__inhalt';
      koerper.appendChild(text);
      akk.append(kopfBox, koerper);
      karte.appendChild(akk);
      if (ziel) disclosure(akk, kopfBox, ziel, koerper, false);
      else akk.setAttribute('open', '');
    });
  }

  let nr = 0;
  function mehr(scope) {
    scope.querySelectorAll('.hw-seitenkopf .hw-lead').forEach((el) => {
      if (el.dataset.hwMehr || el.textContent.trim().length < 280) return;
      el.dataset.hwMehr = '1';
      el.id = el.id || 'hw-mehr-' + ++nr;
      el.classList.add('hw-mehr');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hw-mehr__btn';
      btn.textContent = 'Weiterlesen';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', el.id);
      btn.addEventListener('click', () => {
        const offen = el.classList.toggle('is-offen');
        btn.setAttribute('aria-expanded', String(offen));
        btn.textContent = offen ? 'Weniger anzeigen' : 'Weiterlesen';
      });
      el.after(btn);
    });
  }

  function start(scope) {
    const s = scope || document;
    s.querySelectorAll('.product-details rte-formatter, .product-details .rte, .blog-post-content rte-formatter')
      .forEach((el) => {
        if (el.querySelector('h2, h3')) baue(el);
      });
    aussagen(s);
    mehr(s);
  }

  // Horizon rendert die Produktinfo beim Variantenwechsel per Morph neu (Server-HTML ohne Umbau):
  // dann einmal pro Frame neu aufbauen. Bereits umgebaute Wurzeln überspringt start() (data-hw-akk).
  // Beobachtet wird weiter document.body (Morph, Section-Rendering und Quick-Add können die
  // Produktinfo samt Section ersetzen), reagiert aber nur auf Änderungen in oder mit .product-details;
  // Warenkorb, Header, Pop-ups usw. lösen keinen Neuaufbau mehr aus.
  // Änderungen innerhalb des Aufguss-Timers ([data-hw-zub], assets/hw-timer.js: Countdown im
  // Sekundentakt, Knöpfe, Live-Region) zählen nicht (HWSHOP-80). Wird der Timer-Block selbst
  // ersetzt (Morph, Section-Rendering), liegt das Ziel außerhalb und zählt weiter.
  const PRODUKT = '.product-details';
  const TIMER = '[data-hw-zub]';
  const betrifftProdukt = (n) => n.nodeType === 1 && (n.matches(PRODUKT) || !!n.querySelector(PRODUKT));
  const relevant = (liste) => liste.some((m) => {
    if (m.target.nodeType !== 1) return [...m.addedNodes].some(betrifftProdukt);
    if (m.target.closest(TIMER)) return false;
    return !!m.target.closest(PRODUKT) || [...m.addedNodes].some(betrifftProdukt);
  });
  let geplant = false;
  const neuAufbauen = (liste) => {
    if (geplant || !relevant(liste)) return;
    geplant = true;
    requestAnimationFrame(() => {
      geplant = false;
      start();
    });
  };
  const beobachten = () => {
    if (!document.querySelector('.product-details') || !('MutationObserver' in window)) return;
    new MutationObserver(neuAufbauen).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { start(); beobachten(); });
  else { start(); beobachten(); }
  document.addEventListener('shopify:section:load', (e) => start(e.target));
})();
